// StandingsTable — the sortable P/W/D/L/GF/GA/GD/Pts table shared by League
// Table and Form Table (identical structure; they differ only in where the rows
// come from). Emits the exact DOM the legacy renderers produce *after*
// initSortableTable: the rank "#" column plus `sortable` / `sort-asc` /
// `sort-desc` header classes. Sorting is the useSortableTable hook (replacing
// the imperative initSortableTable); row reordering animates via useFlipRows.

import { useRef } from "react";
import { LEAGUE_COLUMNS, type LeagueRow } from "@bento/shared";
import { useSortableTable } from "../../hooks/useSortableTable.js";
import { useFlipRows } from "../../hooks/useFlipRows.js";

export interface StandingsTableProps {
  rows: LeagueRow[];
  /**
   * Changes when the underlying data changes (season, or venue+timeline, or
   * fixture) so an active sort resets to the natural order — mirroring the
   * legacy initSortableTable re-run on each body render.
   */
  resetKey: unknown;
}

// Sortable value per column: displayed team label for text, raw numbers
// otherwise (GD sorts by its raw value, matching parseFloat("+56")). Module
// scope so its identity is stable across renders.
function cellValue(row: LeagueRow, key: string): string | number {
  switch (key) {
    case "team":
      return row.label;
    case "p":
      return row.p;
    case "w":
      return row.w;
    case "d":
      return row.d;
    case "l":
      return row.l;
    case "gf":
      return row.gf;
    case "ga":
      return row.ga;
    case "gd":
      return row.gd;
    case "pts":
      return row.pts;
    default:
      return "";
  }
}

export default function StandingsTable({
  rows: data,
  resetKey,
}: StandingsTableProps) {
  const { rows, sort, toggle, reset } = useSortableTable(
    data,
    LEAGUE_COLUMNS,
    cellValue,
    resetKey
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  useFlipRows(
    wrapperRef,
    rows.map((r) => r.name).join(",")
  );

  return (
    <div className="data-wrapper top-align" ref={wrapperRef}>
      <div className="data-row header">
        <div className="col-rank" onClick={reset}>
          #
        </div>
        {LEAGUE_COLUMNS.map((col) => {
          const active = sort.key === col.key;
          const indicator = active ? (sort.asc ? " sort-asc" : " sort-desc") : "";
          return (
            <div
              key={col.key}
              className={`${col.cls} sortable${indicator}`}
              onClick={() => toggle(col)}
            >
              {col.label}
            </div>
          );
        })}
      </div>

      {rows.map((r, i) => (
        <div
          key={r.name}
          data-flip-key={r.name}
          className={`data-row${r.highlighted ? " highlighted" : ""}`}
        >
          <div className="col-rank">{i + 1}</div>
          <div className="col-team">{r.label}</div>
          <div className="col-num">{r.p}</div>
          <div className="col-num col-opt">{r.w}</div>
          <div className="col-num col-opt">{r.d}</div>
          <div className="col-num col-opt">{r.l}</div>
          <div className="col-num col-wide">{r.gf}</div>
          <div className="col-num col-wide">{r.ga}</div>
          <div className="col-num">{r.gdText}</div>
          <div className="col-num pts">{r.pts}</div>
        </div>
      ))}
    </div>
  );
}
